import { Form } from '@edgebox/react-components';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import { faArrowDown, faComments, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MonitoringPromptKpi,
  MonitoringPromptStatus,
  MonitoringPromptType,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
  TaxonomyTermStatus,
} from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptRunEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { sendToParent } from '../../../frame-messages';
import {
  searchOfBrandPresenceScreenAddress,
  toBrandPresenceSearch,
  useBrandPresenceScreenAddress,
  useReportBrandPresenceScreenQuery,
} from './brandPresenceScreenQuery';
import { GovernanceAiAnswerDetail } from './Components/GovernanceAiAnswerDetail';
import { MonitoringPromptRunStatusBadge } from './Components/MonitoringPromptRunDetail';
import { SentimentThumb } from './Components/SentimentThumb';
import { EntityShareAndSentiment } from './Components/EntityShareAndSentiment';
import { EntityFavicon, FaviconLoadGate } from './Components/SourceFavicon';
import { EntityDomainMap, useEntityDomains } from './Components/useEntityDomains';
import { SOURCES_COLUMN_HINT, SourceGroupingToggle, SourcesCell } from './Components/SourceChips';
import { useAuthoritySources } from './Components/useAuthoritySources';
import {
  AuthoritySourceIndex,
  CitationHostRowLike,
  CitationSourceGroup,
  groupCitationHosts,
  resolveSourceLabel,
  SOURCE_CHIP_LIMIT,
  selectSourceChips,
  SourceGrouping,
} from './Components/citationSources.utils';
import { buildTermFilterOptions, termStatusPresentation } from './Components/termStatusPresentation';
import { PurposeTermLike } from './Components/purposeTabs.utils';
import { isKpiShown, purposeKpiMask } from './Components/purposeKpiMask.utils';
import { TONE_KPI } from './toneMetric';
import { PurposeKpiScopeNote } from './Components/PurposeKpiScopeNote';
import { GovernanceAnswerExportModal } from './Components/GovernanceAnswerExportModal';
import { SuggestedTermIcon, useTaxonomyTermName } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { resolveTaxonomyFacetPresentation, TaxonomyTermName } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { EngineLogo, hasEngineLogo } from './Components/EngineLogo';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import { BRAND_PRESENCE_TAB_IN_STRIP_CLASSES, getBrandPresenceSubTabClassName } from './Components/BrandPresenceTabStyles';
import {
  BP_CARD_CLASSES,
  BP_TABLE_HEAD_CELL_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_SMALL_CLASSES,
} from './Components/brandPresenceClasses';

import { getCachedTaxonomyTermsByFacet } from './Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import {
  AiAnswersFilterState,
  EMPTY_AI_ANSWERS_FILTERS,
  MentionBadge,
  PromptLabel,
  PromptLabelSource,
  PromptDisplay,
  RecommendationFilter,
  VisibilityFilter,
  UNKNOWN_PROMPT_LABEL,
  buildMentionBadges,
  buildMentionShareSegments,
  MentionShareSegment,
  mentionTermKey,
  buildPromptLabelIndex,
  buildFailedCountFilters,
  buildRunListFilters,
  createPromptLabelResolver,
  promptRevisionId,
  findEnglishLocaleKey,
  formatRelativeTime,
  translationTextFor,
  parseAiAnswersFilterState,
  ORIGINAL_LANGUAGE_HINT,
  isDisplayOnlyChange,
  isPendingRunStatus,
  pickPromptText,
  shouldMarkOriginalLanguage,
  resolveWinner,
  serializeAiAnswersFilterState,
} from './GovernanceAnalyticsAiAnswers.utils';

/** The label that leads the filter bar. */
const FILTER_LABEL_CLASSES = 'font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500';

/**
 * The switch of one filter's choices: where they are wider than the card, they scroll sideways in the
 * switch rather than running past the card's edge.
 */
const FILTER_CHOICE_STRIP_CLASSES =
  'inline-flex min-w-0 max-w-full items-center gap-0.5 overflow-x-auto rounded-xl bg-slate-100/90 p-1 [scrollbar-width:none]';

/**
 * A message in place of the table's rows: as wide as the part of the table in view and held at its
 * left edge, so it reads centred on a narrow screen without scrolling the table sideways.
 */
const MESSAGE_IN_VIEW_CLASSES = 'sticky left-0 w-[100cqw]';

/** Rows drawn while answers load — about one screen of them. */
const ANSWER_SKELETON_ROW_COUNT = 6;

/** The padding of every cell of an answer row, and of the rows drawn while answers load. */
const ANSWER_CELL_CLASSES = 'px-4 py-2.5';

/**
 * The fixed share of the table's width each column takes: When, Prompt, Engine, Winner, Mentions,
 * Sources, and Status while failed answers are shown (the table scales them back to its width).
 */
const ANSWER_COLUMN_WIDTH_CLASSES = ['w-[7%]', 'w-[29%]', 'w-[12%]', 'w-[11%]', 'w-[27%]', 'w-[14%]'] as const;
const STATUS_COLUMN_WIDTH_CLASS = 'w-[10%]';

/** The column widths of the answers table, and of its loading skeleton. */
function AnswerColumns({ withStatus }: { withStatus?: boolean }) {
  return (
    <colgroup>
      {ANSWER_COLUMN_WIDTH_CLASSES.map((widthClass, column) => (
        <col key={column} className={widthClass} />
      ))}
      {withStatus ? <col className={STATUS_COLUMN_WIDTH_CLASS} /> : null}
    </colgroup>
  );
}

/** One loading answer, drawn in the shape of a row: when, the prompt, the engine, the winner, the mentions, the sources. */
function AnswerSkeletonCells({ row }: { row: number }) {
  return (
    <>
      <td className={ANSWER_CELL_CLASSES}>
        <SkeletonBlock className="h-3.5 w-14" />
      </td>
      <td className={ANSWER_CELL_CLASSES}>
        <SkeletonBlock className={`h-4 ${row % 2 ? 'w-3/4' : 'w-11/12'}`} />
        <SkeletonBlock className="mt-2 h-3 w-28" />
      </td>
      <td className={ANSWER_CELL_CLASSES}>
        <div className="flex items-center gap-2">
          <SkeletonBlock className="h-[18px] w-[18px] rounded-md" />
          <SkeletonBlock className="h-3.5 w-16" />
        </div>
      </td>
      <td className={ANSWER_CELL_CLASSES}>
        <SkeletonBlock className="h-3.5 w-20" />
      </td>
      <td className={ANSWER_CELL_CLASSES}>
        <SkeletonBlock className="h-1.5 w-40 rounded-full" />
        <div className="mt-2 flex gap-3">
          <SkeletonBlock className="h-3.5 w-16" />
          <SkeletonBlock className="h-3.5 w-20" />
        </div>
      </td>
      <td className={ANSWER_CELL_CLASSES}>
        <div className="flex items-center gap-2">
          <div className="flex">
            <SkeletonBlock className="h-5 w-5 rounded-full" />
            <SkeletonBlock className="-ms-1.5 h-5 w-5 rounded-full" />
            <SkeletonBlock className="-ms-1.5 h-5 w-5 rounded-full" />
          </div>
          <SkeletonBlock className="h-3.5 w-16" />
        </div>
      </td>
    </>
  );
}

const ANSWER_SKELETON_TABLE_ROWS = Array.from({ length: ANSWER_SKELETON_ROW_COUNT }, (_unused, row) => (
  <tr key={`skeleton-${row}`} aria-hidden="true">
    <AnswerSkeletonCells row={row} />
  </tr>
));

const ANSWER_SKELETON_ROWS = (
  <table className="w-full min-w-[60rem] table-fixed">
    <AnswerColumns />
    <tbody className="divide-y divide-slate-100">{ANSWER_SKELETON_TABLE_ROWS}</tbody>
  </table>
);

/** Translates a MonitoringPromptCountry enum value to its display name, falling back to the raw code. */
function translateCountry(country?: string): string {
  if (!country) {
    return '';
  }
  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptCountry', country as never);
  } catch {
    return String(country);
  }
}

const PAGE_SIZE = 25;

/** Explains what the prompt-language toggle does, in the reader's terms. */
const PROMPT_LANGUAGE_HINT =
  'Prompts are shown in English when the template has an English version. Tick this to read each prompt in the language it was asked in.';

/**
 * Statuses worth listing in the Prompt filter. The endpoint defaults to Active only, and a run can
 * still point at a template that has since been paused or archived.
 *
 * `PreviousRevision` is deliberately absent: the endpoint rejects it without the
 * `MonitoringPrompt_ReadAll` scope, and a superseded revision shares its key with the live one.
 */
const PROMPT_FILTER_STATUSES: MonitoringPromptStatus[] = [
  MonitoringPromptStatus.Active,
  MonitoringPromptStatus.Paused,
  MonitoringPromptStatus.Draft,
  MonitoringPromptStatus.Archived,
];

/** The server clamps `itemsPerPage` to 10x the endpoint default (50), so 500 is the real ceiling. */
const PROMPT_PAGE_SIZE = 500;

/** Hard stop so a pathological project cannot spin the loader forever. */
const PROMPT_MAX_PAGES = 20;

/**
 * Prompt TEMPLATES, for the Prompt filter dropdown.
 *
 * Instances cannot be listed at all: `monitoring-prompt.controller.ts` pins
 * `templatePrompt.key: null` into the where clause whenever no `templatePromptKey` filter is
 * given, and every instance has a template parent — so `type=instance` always returns an empty
 * page. Templates are exactly what that default clause does return, and filtering runs by a
 * template key covers all of its per-locale instances anyway.
 */
async function loadPromptTemplates(api: ISyncCoreApi): Promise<PromptLabelSource[]> {
  const collected: PromptLabelSource[] = [];

  for (let page = 0; page < PROMPT_MAX_PAGES; page += 1) {
    const response = await api.governance.monitoringPrompts.list(
      { page, itemsPerPage: PROMPT_PAGE_SIZE },
      { type: [MonitoringPromptType.Template], status: PROMPT_FILTER_STATUSES }
    );

    collected.push(...(response.items || []));

    if (page + 1 >= (response.numberOfPages ?? 1)) {
      break;
    }
  }

  return collected;
}

/** A term as the cached facet fetch returns it, before it becomes a filter choice. */
type SelectableTerm = { key?: unknown; name?: unknown; status?: TaxonomyTermStatus };

/** A stable machine key from a runtime/serialized revision reference. */
function referenceKey(reference: unknown): string {
  if (typeof reference === 'string') {
    return reference.trim();
  }
  if (!reference || typeof reference !== 'object') {
    return '';
  }
  const record = reference as { getStableKey?: () => string; key?: unknown };
  return String(record.getStableKey?.() || record.key || '').trim();
}

/** What a reader is told about an answer that does not mention their brand at all. */
const YOU_INVISIBLE_LABEL = 'You · invisible';

/** The neutral fills of the competitors' segments of a share bar, strongest first; "You" alone is coloured. */
const COMPETITOR_SHARE_FILLS = ['bg-slate-400', 'bg-slate-300', 'bg-slate-200'] as const;

/**
 * The thin share bar over a row's mentions: one segment per mentioned brand, as wide as its share of
 * voice. "You" is the one segment in the primary colour; every competitor is a neutral grey, so the
 * bar says at a glance how much of the answer was about the reader. Decorative: the figures are
 * printed beside the names under it.
 */
function MentionShareBar({ segments }: { segments: readonly MentionShareSegment[] }) {
  let competitorIndex = 0;
  // Shares are measured per brand and can add up to a little over the whole; the bar is then scaled
  // to its width, and anything short of the whole stays as the empty track.
  const scale = Math.max(
    100,
    segments.reduce((sum, segment) => sum + segment.sovPercent, 0)
  );
  return (
    <div aria-hidden className="flex h-1.5 w-full max-w-[12rem] min-w-[6rem] flex-1 gap-px overflow-hidden rounded-full bg-slate-100">
      {segments.map((segment, index) => {
        const fill = segment.isYou ? 'bg-primary' : COMPETITOR_SHARE_FILLS[Math.min(competitorIndex++, COMPETITOR_SHARE_FILLS.length - 1)];
        // A segment's width is the brand's measured share, a runtime value no class can carry.
        return (
          <span key={`${segment.name}-${index}`} className={`h-full ${fill}`} style={{ width: `${(segment.sovPercent / scale) * 100}%` }} />
        );
      })}
    </div>
  );
}

/**
 * The mark of an answer that does not mention the reader's brand at all, beside the share bar.
 *
 * Deliberately NOT masked with the visibility KPI. This is the visibility measurement at a sample
 * of one, and it is exactly where that reading still carries information: on a prompt that names
 * the brand, visibility is 1.000 by construction, so an answer that fails to mention it at all is a
 * genuine anomaly — the kind a reader needs to see, not a KPI to average. The struck-through eye
 * says it without the colour.
 */
function YouInvisibleMark() {
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold leading-4 text-rose-700">
      <FontAwesomeIcon icon={faEyeSlash} className="text-[10px]" aria-hidden />
      {YOU_INVISIBLE_LABEL}
    </span>
  );
}

/**
 * One mentioned brand, as plain text: its name, its share of voice and a small verdict mark.
 *
 * Only "You" is set apart, in the primary colour; a competitor reads as ordinary text so the row
 * does not shout. The share and the thumb render through the same component the answer page uses,
 * so a mention reads alike on both.
 */
function MentionEntry({
  badge,
  api,
  showShare,
  showSentiment,
}: {
  badge: MentionBadge;
  api?: ISyncCoreApi;
  /**
   * Whether share of voice is a measurement for the purpose this list is filtered to.
   *
   * The same question the answer detail asks of the same shared component. A row and the page it
   * opens report one run, so a percentage the detail refuses to print may not survive in the list.
   */
  showShare: boolean;
  /** Same, for sentiment. */
  showSentiment: boolean;
}) {
  // Resolved through the shared term cache, so a key already rendered elsewhere on the page costs
  // no extra request. Hooks must run before any early return.
  const termKey = badge.kind === 'entity' ? badge.termKey : undefined;
  const { termStatus } = useTaxonomyTermName(api, termKey ?? '');

  if (badge.kind === 'you-invisible') {
    // Drawn beside the share bar by `MentionsCell`, not among the names.
    return null;
  }

  if (badge.kind === 'more') {
    return <li className="whitespace-nowrap text-slate-500">+{badge.count} more</li>;
  }

  const presentation = termStatusPresentation(termStatus, 'badge');
  // An archived term is not part of what the customer is looking at, so the mention disappears
  // rather than muting. The mention itself is untouched server-side.
  if (presentation.hidden) {
    return null;
  }
  return (
    <li
      className={['inline-flex min-w-0 max-w-full items-center gap-1 whitespace-nowrap', presentation.className].filter(Boolean).join(' ')}
      // The tooltip is the same figure spelled out, so it is masked with it — otherwise the number
      // the row refuses to print comes back on hover.
      title={presentation.title ?? (showShare ? `${badge.name} · ${badge.sovPercent}% share of voice` : badge.name)}
    >
      <span className={`min-w-0 truncate ${badge.isYou ? 'font-semibold text-primary' : 'font-medium text-slate-700'}`}>
        {badge.isYou ? 'You' : badge.name}
      </span>
      {presentation.showSuggestionIcon ? <SuggestedTermIcon spacing="flush" /> : null}
      <EntityShareAndSentiment
        sovPercent={badge.sovPercent}
        showShare={showShare}
        tone={showSentiment ? badge.sentimentTone : undefined}
        percentClassName="tabular-nums text-slate-500"
        thumbClassName="text-[10px]"
      />
    </li>
  );
}

/**
 * The Mentions column: a thin share bar, then the top brands as plain text with their share and a
 * verdict mark, and how many more the answer named. The bar is drawn only where the purpose
 * measures share of voice, like the figures it stands for.
 */
function MentionsCell({
  run,
  api,
  showShare,
  showSentiment,
}: {
  run: ClientMonitoringPromptRunEntity;
  api?: ISyncCoreApi;
  showShare: boolean;
  showSentiment: boolean;
}) {
  const badges = useMemo(() => buildMentionBadges(run.entities || [], { status: run.status }), [run]);
  const segments = useMemo(() => buildMentionShareSegments(run.entities || [], { status: run.status }), [run]);

  if (!badges.length) {
    return null;
  }

  const youInvisible = badges.some((badge) => badge.kind === 'you-invisible');
  const named = badges.filter((badge) => badge.kind !== 'you-invisible');
  const showBar = showShare && segments.length > 0;

  return (
    <div className="min-w-0">
      {showBar || youInvisible ? (
        <div className="mb-1 flex h-4 items-center gap-2">
          {showBar ? <MentionShareBar segments={segments} /> : null}
          {youInvisible ? <YouInvisibleMark /> : null}
        </div>
      ) : null}
      {named.length ? (
        <ul className="m-0 flex list-none flex-wrap items-center gap-x-3 p-0 text-[0.8125rem] leading-[1.125rem]">
          {named.map((badge, index) => (
            <MentionEntry key={index} badge={badge} api={api} showShare={showShare} showSentiment={showSentiment} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * The winner column: the recommended brand's name with its small logo, "You" in the primary colour
 * and a competitor in neutral text.
 *
 * Deliberately renders the name straight from the run payload rather than resolving the term: this
 * reports what the AI actually recommended, which is a measurement about the answer, not a claim
 * about the customer's taxonomy. Muting it would qualify a finding that is not in doubt, and hiding
 * an archived winner would erase the finding altogether. If this is ever switched to a term
 * resolver, it must pass `neverMute`.
 *
 * Deliberately NOT masked by the purpose either, on the same reading: it names who this one answer
 * recommended, which is a fact about the answer rather than a KPI aggregated over answers. (No
 * derived purpose type suppresses recommendation in any case; only a hand-written `custom` list
 * could, and there the column would still be reporting what the answer said.)
 */
function WinnerCell({
  run,
  api,
  entityDomains,
}: {
  run: ClientMonitoringPromptRunEntity;
  api?: ISyncCoreApi;
  entityDomains?: EntityDomainMap;
}) {
  const winner = resolveWinner(run.entities, run.explicitRecommendation?.evidence);

  if (!winner) {
    return (
      <span className="text-slate-400" aria-label="No winner" title="No winner">
        —
      </span>
    );
  }

  const isOwn = winner.ownership === 'own';
  const winnerEntity = (run.entities || []).find((entity) => entity.recommendation === 1);
  const winnerTermKey = winnerEntity ? mentionTermKey(winnerEntity) : undefined;
  return (
    <span
      className={`inline-flex min-w-0 max-w-full items-center gap-1.5 text-[0.8125rem] ${isOwn ? 'font-semibold text-primary' : 'font-medium text-slate-700'}`}
      title={winner.evidence || undefined}
    >
      {/* Absent whenever the domain is unknown, which is the common case. */}
      <EntityFavicon api={api} domain={winnerTermKey ? entityDomains?.get(winnerTermKey)?.domain : undefined} />
      <span className="min-w-0 truncate">{isOwn ? 'You' : winner.name}</span>
    </span>
  );
}

/**
 * The engine column: the engine's logo and its name. An engine this app carries no logo for shows
 * the facet's own mark instead, so every row reads the same way.
 */
function EngineCell({ api, engineKey }: { api?: ISyncCoreApi; engineKey: string }) {
  const facetIcon = resolveTaxonomyFacetPresentation(TaxonomyFacet.Engine)?.icon;
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-2 break-words text-[0.8125rem] font-medium text-slate-700">
      {hasEngineLogo(engineKey) ? (
        <EngineLogo termKey={engineKey} size="badge" />
      ) : (
        <span
          aria-hidden
          className="inline-flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-500"
        >
          {facetIcon ? <FontAwesomeIcon icon={facetIcon} className="text-[10px]" /> : null}
        </span>
      )}
      <TaxonomyTermName api={api} termKey={engineKey} className="min-w-0 leading-snug" />
    </span>
  );
}

export function GovernanceAnalyticsAiAnswers() {
  const { runId } = useParams();

  if (runId) {
    return <GovernanceAiAnswerDetail runId={runId} />;
  }

  return <AiAnswersList />;
}

function AiAnswersList() {
  const navigate = useNavigate();
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  // The one address the filters are read from: the host page's while it names this screen, the
  // frame's own only in the moment right after an in-frame navigation wrote it. The frame's address
  // does not follow the filter changes this screen sends with `update-query`, so reading it while
  // the host page names the screen would hand back the filters as they stood on arrival.
  const screenAddress = useBrandPresenceScreenAddress();
  const incomingFilters = useMemo(
    () => parseAiAnswersFilterState(searchOfBrandPresenceScreenAddress(screenAddress), screenAddress.query),
    [screenAddress]
  );

  const [optionsLoading, setOptionsLoading] = useState(true);
  // The raw terms are held, and the options derived from them, so that a filter value pointing at a
  // since-archived term can be kept as a visible choice without re-fetching when the selection moves.
  // Purpose terms carry their type and KPI list too, because this screen's filter tabs are masked
  // by what the selected purpose measures — not only named by it.
  const [purposeTerms, setPurposeTerms] = useState<PurposeTermLike[]>([]);
  const [engineTerms, setEngineTerms] = useState<SelectableTerm[]>([]);
  const [localeTerms, setLocaleTerms] = useState<SelectableTerm[]>([]);
  const [promptOptions, setPromptOptions] = useState<PromptLabel[]>([]);

  const [filters, setFilters] = useState<AiAnswersFilterState>(incomingFilters);
  const incomingFiltersSerialized = useMemo(() => JSON.stringify(incomingFilters), [incomingFilters]);
  // The address is the source while it names this screen: a Back, a handover or a shared link
  // re-applies exactly what it carries, and a filter it has dropped stays dropped.
  useEffect(() => {
    setFilters((current) => (JSON.stringify(current) === incomingFiltersSerialized ? current : incomingFilters));
  }, [incomingFilters, incomingFiltersSerialized]);
  const screenQuery = useMemo(() => serializeAiAnswersFilterState(filters), [filters]);
  useReportBrandPresenceScreenQuery(screenQuery);
  const [runs, setRuns] = useState<ClientMonitoringPromptRunEntity[]>([]);
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [runsLoading, setRunsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showExport, setShowExport] = useState(false);

  // Row labels are resolved per prompt revision through the same path the detail page uses — the
  // list endpoint structurally cannot return instances (see loadPromptTemplates). This table lists
  // answers, not prompts: a row keeps pointing at the revision that produced it long after that
  // prompt was paused or archived, so it must not resolve through the Active-only by-key lookup.
  // The prompt picker above is a separate, deliberately status-filtered fetch. Created once so its
  // cache survives paging.
  const promptLabelResolver = useMemo(() => createPromptLabelResolver(), []);
  const [promptLabels, setPromptLabels] = useState<Map<string, string>>(new Map());
  /** How many answers the "Include failed" toggle is currently hiding. */
  const [failedCount, setFailedCount] = useState<number | undefined>(undefined);

  // Locale display names, joined from the same locale terms loaded for the filter bar — so
  // the list never renders a raw locale key and never fetches per row.
  // Archived terms are already excluded by the fetch. Deriving here keeps a still-selected one — a
  // saved view or a deep link — as a visible choice, and marks suggestions.
  // Scoped to the selected purpose, which is what the endpoint accepts. With "All purposes" the
  // question is not well-formed, so no favicons resolve — the same thing the reader sees for any
  // entity the inference declined.
  const entityDomains = useEntityDomains(api, filters.purposeTermKey);

  // The curated `authority-source` terms, so a chip reads as the operator's name for a publisher
  // rather than as its hostname. Shared with the answer detail and the Analytics Sources module,
  // through one cached fetch.
  const { index: authoritySources, ready: sourcesReady } = useAuthoritySources(api);

  /** True when a chip is the very source the list is currently filtered to. */
  const isActiveSource = React.useCallback(
    (group: CitationSourceGroup, termKey?: string) => {
      if (filters.citedTermKey && termKey && filters.citedTermKey === termKey) {
        return true;
      }
      if (filters.citedDomain && group.key === filters.citedDomain) {
        return true;
      }
      // A hostname filter also lights up the domain group that contains that hostname, so arriving
      // from a full-hostname link does not leave the grouped view with nothing highlighted.
      return !!filters.citedHost && (group.key === filters.citedHost || group.hosts.includes(filters.citedHost));
    },
    [filters.citedTermKey, filters.citedDomain, filters.citedHost]
  );

  const purposeOptions = useMemo(
    () => buildTermFilterOptions(purposeTerms, filters.purposeTermKey),
    [purposeTerms, filters.purposeTermKey]
  );

  /**
   * The purpose the list is filtered to, and what it measures.
   *
   * "All purposes" resolves to no term and therefore to no mask, which is the honest reading: the
   * list is then showing answers from purposes with different shapes, and nothing states which KPIs
   * are meaningful across all of them. That path renders exactly what it renders today.
   */
  const selectedPurposeTerm = useMemo(
    () => (filters.purposeTermKey ? purposeTerms.find((term) => String(term?.key ?? '').trim() === filters.purposeTermKey) : undefined),
    [purposeTerms, filters.purposeTermKey]
  );
  const kpiMask = useMemo(() => purposeKpiMask(selectedPurposeTerm), [selectedPurposeTerm]);
  /**
   * True while a purpose is selected but its tag has not arrived yet.
   *
   * Until it does, what that purpose measures is unknown — not "everything". Offering the filters in
   * that window would flash a control that is about to disappear, and asking the server with one of
   * them applied would return a page narrowed by a filter the reader is never shown. The screen
   * already waits for these terms before drawing the table; the filters wait with it.
   */
  const purposeMaskPending = !!filters.purposeTermKey && optionsLoading;
  /**
   * True once the tags have arrived and the selected key names none of them.
   *
   * A stale link, or a purpose since archived or deleted. The key is not a purpose the reader can
   * see: it is absent from the dropdown, which therefore already reads "All purposes" while the
   * request still carries it — and Sync Core, which resolves the key server-side, blanks whatever
   * that purpose does not measure. The screen would then draw columns for fields the server has
   * emptied, so the reader meets dashes where a measurement belongs.
   *
   * Resolved by dropping the key rather than by falling back to a purpose (as the AI-visibility tabs
   * do). There the tabs offer no unfiltered view, so the first tab is the only landing place; here
   * "All purposes" is a real, offered state, it is already what the dropdown shows, and it is the
   * only one that keeps the dropdown, the request and the mask saying the same thing. Substituting
   * some other purpose would instead answer a question the reader never asked.
   */
  const purposeKeyUnknown = !!filters.purposeTermKey && !optionsLoading && !selectedPurposeTerm;
  const showsVisibilityFilter = !purposeMaskPending && isKpiShown(kpiMask, MonitoringPromptKpi.Visibility);
  const showsRecommendationFilter = !purposeMaskPending && isKpiShown(kpiMask, MonitoringPromptKpi.Recommendation);
  const showsAttributes = !purposeMaskPending && isKpiShown(kpiMask, MonitoringPromptKpi.Attributes);
  // The two mention-badge measures, asked exactly as the answer detail asks them of the same
  // component — the list and the page it opens report the same run and may not disagree.
  const showsShareOfVoice = !purposeMaskPending && isKpiShown(kpiMask, MonitoringPromptKpi.ShareOfVoice);
  const showsSentiment = !purposeMaskPending && isKpiShown(kpiMask, TONE_KPI);
  const engineOptions = useMemo(() => buildTermFilterOptions(engineTerms, filters.engineKey), [engineTerms, filters.engineKey]);
  const localeOptions = useMemo(() => buildTermFilterOptions(localeTerms, filters.localeKey), [localeTerms, filters.localeKey]);

  const localeNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of localeOptions) {
      map.set(option.key, option.name);
    }
    return map;
  }, [localeOptions]);

  // The resolver is built once per api so its cache survives paging; it reads the locale names
  // through a ref because those arrive from a separate request that may land later.
  /** The locale Sync Core should translate into. Empty when the project tracks no English locale. */
  const englishLocaleKey = useMemo(() => findEnglishLocaleKey(localeOptions), [localeOptions]);

  const updateFilters = (patch: Partial<AiAnswersFilterState>) => {
    setFilters((current) => {
      const next = { ...current, ...patch };
      // Narrowing the result set invalidates the reader's position in it, so it resets to page one.
      // Changing how the SAME results are drawn does not: a display toggle that threw the reader
      // back to page one — as the language switch did — loses their place for nothing.
      if (!('page' in patch) && !isDisplayOnlyChange(patch)) {
        next.page = 0;
      }
      sendToParent({ type: 'update-query', query: serializeAiAnswersFilterState(next) });
      return next;
    });
  };

  /**
   * Clicking a chip filters the list to that source; clicking the active chip clears the filter.
   *
   * A curated source filters by its TERM, which the server expands through the term's name and
   * aliases — so the filter keeps covering every hostname the operator has attached to it. An
   * uncurated source has only itself to filter by, and does so on the dimension the reader is
   * currently looking at.
   */
  const selectSource = (group: CitationSourceGroup, termKey: string | undefined, active: boolean) => {
    const cleared = { citedHost: '', citedDomain: '', citedTermKey: '' };
    if (active) {
      updateFilters(cleared);
      return;
    }
    if (termKey) {
      updateFilters({ ...cleared, citedTermKey: termKey });
      return;
    }
    updateFilters(filters.sourceGrouping === 'host' ? { ...cleared, citedHost: group.key } : { ...cleared, citedDomain: group.key });
  };

  // Load the filter-bar options once.
  useEffect(() => {
    if (!api) {
      setOptionsLoading(false);
      return;
    }

    let active = true;
    void (async () => {
      const [purposeTerms, engineTerms, localeTerms, prompts] = await Promise.all([
        getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.MonitoringPromptPurpose),
        getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.Engine),
        getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.Locale),
        // Runs reference per-locale prompt *instances*, so we resolve names against the
        // instance list (not templates); their `promptText` is the question a run answered.
        loadPromptTemplates(api),
      ]);

      if (!active) {
        return;
      }

      // Archived terms are already excluded by the fetch; this keeps a still-selected one (a saved
      // view or deep link) so the filter in effect stays visible, and marks suggestions.
      setPurposeTerms(purposeTerms);
      setEngineTerms(engineTerms);
      setLocaleTerms(localeTerms);
      setPromptOptions(Array.from(buildPromptLabelIndex(prompts).values()).sort((a, b) => a.name.localeCompare(b.name)));
      setOptionsLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [api]);

  /**
   * A purpose key naming no loaded tag is dropped, and the list falls back to "All purposes".
   *
   * Written back to the state and the parent's query, not merely ignored below, so a refresh or a
   * re-shared link does not carry the dead key round again. Only after the tags have arrived —
   * before that, "not found" only means "not loaded yet".
   */
  useEffect(() => {
    if (!purposeKeyUnknown) {
      return;
    }
    updateFilters({ purposeTermKey: '' });
    // `updateFilters` closes over nothing that changes the decision; the predicate above is the
    // whole trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purposeKeyUnknown]);

  /**
   * The filter state with anything the purpose does not measure neutralised.
   *
   * A tab group that is not on the screen may not still be narrowing the list: a deep link carrying
   * `recommendation=us` into a purpose that does not measure recommendation would otherwise filter
   * invisibly, and the reader would have no control to undo it. The choice itself stays in state, so
   * moving back to a purpose that measures it restores what they picked.
   *
   * The two drill-down term keys are neutralised by the same rule, and must be: `buildRunListFilters`
   * ends its recommendation branch with a bare `else if (state.recommendedTermKey)`, so blanking the
   * tab alone would route the identical narrowing through the term key instead of removing it. Both
   * keys arrive by deep link from the Gaps drill-down and have no control of their own on this
   * screen, which is exactly why an unusable one has to be dropped rather than left to filter.
   */
  const maskedFilters = useMemo<AiAnswersFilterState>(
    () => ({
      ...filters,
      visibility: showsVisibilityFilter ? filters.visibility : 'all',
      recommendation: showsRecommendationFilter ? filters.recommendation : 'all',
      recommendedTermKey: showsRecommendationFilter ? filters.recommendedTermKey : '',
      aspectTermKey: showsAttributes ? filters.aspectTermKey : '',
    }),
    [filters, showsVisibilityFilter, showsRecommendationFilter, showsAttributes]
  );

  /**
   * The question this screen asks the server, and nothing else.
   *
   * Derived separately from the UI state so the fetch below can depend on the QUESTION rather than
   * on every piece of state the screen holds. Toggling a display-only choice leaves this identical,
   * so no request goes out and the reader keeps their page.
   */
  const runListFilters = useMemo(() => buildRunListFilters(maskedFilters, englishLocaleKey), [maskedFilters, englishLocaleKey]);
  const runListFiltersKey = useMemo(() => JSON.stringify(runListFilters), [runListFilters]);
  const failedCountFilters = useMemo(() => buildFailedCountFilters(maskedFilters), [maskedFilters]);

  // Fetch the run page whenever the question — or the page — changes.
  useEffect(() => {
    // Nothing is asked until the selected purpose's tag has arrived: the question is not yet
    // well-formed, because a filter this purpose does not measure would still be in it. Nor while a
    // dead purpose key is still in the state — the effect above is clearing it, and asking with it
    // would spend a request on a purpose-filtered page the screen is about to replace.
    if (!api || purposeMaskPending || purposeKeyUnknown) {
      return;
    }

    let active = true;
    setRunsLoading(true);
    setError(undefined);

    void (async () => {
      try {
        const response = await api.governance.monitoringPromptRuns.list({ page: filters.page, itemsPerPage: PAGE_SIZE }, runListFilters);

        if (!active) {
          return;
        }

        const items = response.items || [];
        setRuns(items);
        setNumberOfPages(Math.max(1, response.numberOfPages ?? 1));

        // Distinct prompt keys on this page only (<= PAGE_SIZE), resolved in parallel and cached.
        // One cheap count so the toggle can say how many answers it is hiding. Failures here are
        // not worth surfacing — the label simply omits the count.
        void api.governance.monitoringPromptRuns
          .list({ page: 0, itemsPerPage: 1 }, failedCountFilters)
          .then((failed) => {
            if (active) {
              setFailedCount(failed.totalNumberOfItems ?? undefined);
            }
          })
          .catch(() => undefined);

        const labels = await promptLabelResolver.resolve(items.map((item) => item.prompt));
        if (active) {
          setPromptLabels((current) => new Map([...current, ...labels]));
        }
      } catch (loadError) {
        if (!active) {
          return;
        }
        setError('Failed to load AI answers.');
        setRuns([]);
        setNumberOfPages(1);
      } finally {
        if (active) {
          setRunsLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
    // Keyed on the serialized question and the page — NOT on the whole filter state. Depending on
    // the state object meant every display toggle re-ran the fetch, which is what made switching
    // the prompt language feel like a page reload. `englishLocaleKey` is folded into the key, so a
    // locale list that lands after the first render now triggers exactly one refetch instead of
    // never being asked for at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, runListFiltersKey, filters.page, promptLabelResolver, purposeMaskPending, purposeKeyUnknown]);

  // Every filter dimension — purpose, engine, locale, recommendation and own-visibility — is now
  // resolved server-side (see buildRunListFilters), so the fetched page needs no refinement and
  // the pager counts are the true totals.
  const visibleRuns = runs;

  // When / Prompt / Engine / Winner / Mentions / Sources, plus Status only while failed answers are
  // shown. Derived rather than written out, so a new column cannot leave a short empty-state row.
  const COLUMN_COUNT = filters.includeFailed ? 7 : 6;

  /**
   * An answer's own page, keeping the list's filters so Back returns to the same view. The filters
   * as they stand now, not the frame's own address: that address still reads as it did when the
   * screen was entered, because the changes travelled to the host page through `update-query`.
   */
  const answerPath = (run: ClientMonitoringPromptRunEntity) =>
    `/brand-presence/analytics/ai-answers/${run.id}${toBrandPresenceSearch(screenQuery)}`;

  const openRun = (run: ClientMonitoringPromptRunEntity) => {
    navigate(answerPath(run));
  };

  const recommendationTabs: { key: RecommendationFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'us', label: 'Recommends you' },
    { key: 'competitor', label: 'Recommends a competitor' },
    { key: 'none', label: 'No winner' },
  ];
  const visibilityTabs: { key: VisibilityFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'visible', label: 'Visible' },
    { key: 'invisible', label: 'Invisible' },
  ];

  // The row favicons (EntityFavicon in the mention badges, SourceFavicon inside SourcesCell) wait
  // for this: the term lists this list's own filters are built from, and the page of runs itself —
  // not a timer. Before this landed, an uncached favicon competed with those same requests for the
  // pod and the browser's per-origin connection budget.
  const faviconLoadReady = !optionsLoading && !runsLoading;

  return (
    <FaviconLoadGate ready={faviconLoadReady}>
      <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
        <main className="relative max-w-[1800px] min-h-[800px] mx-auto px-6 py-8">
          <BrandPresencePageHeader
            eyebrow={
              <>
                Brand Presence <span className="text-slate-300">/</span> Analytics
              </>
            }
            title="AI answers"
            description="Every answer AI engines gave to your monitored prompts, newest first."
            actions={
              <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} onClick={() => setShowExport(true)}>
                <FontAwesomeIcon icon={faArrowDown} className="text-xs" />
                Export answers
              </button>
            }
          />

          <GovernanceAnswerExportModal
            show={showExport}
            onHide={() => setShowExport(false)}
            pageFilters={{ engineKey: filters.engineKey, localeKey: filters.localeKey, promptKey: filters.promptKey }}
            engineOptions={engineOptions}
            localeOptions={localeOptions}
            promptOptions={promptOptions}
          />

          <div className={`${BP_CARD_CLASSES} mb-6 space-y-3 px-4 py-3 motion-safe:animate-bp-fade-up`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className={FILTER_LABEL_CLASSES}>Filters</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Prompt purpose</span>
                <Form.Select
                  size="sm"
                  value={filters.purposeTermKey}
                  style={{ minWidth: '200px' }}
                  onChange={(event) => updateFilters({ purposeTermKey: event.target.value })}
                >
                  <option value="">All purposes</option>
                  {purposeOptions.map((option) => (
                    <option key={option.key} value={option.key} className={option.presentation.className}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Engine</span>
                <Form.Select
                  size="sm"
                  value={filters.engineKey}
                  style={{ minWidth: '160px' }}
                  onChange={(event) => updateFilters({ engineKey: event.target.value })}
                >
                  <option value="">All engines</option>
                  {engineOptions.map((option) => (
                    <option key={option.key} value={option.key} className={option.presentation.className}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Locale</span>
                <Form.Select
                  size="sm"
                  value={filters.localeKey}
                  style={{ minWidth: '160px' }}
                  onChange={(event) => updateFilters({ localeKey: event.target.value })}
                >
                  <option value="">All locales</option>
                  {localeOptions.map((option) => (
                    <option key={option.key} value={option.key} className={option.presentation.className}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-600">Prompt</span>
                <Form.Select
                  size="sm"
                  value={filters.promptKey}
                  style={{ minWidth: '200px' }}
                  onChange={(event) => updateFilters({ promptKey: event.target.value })}
                >
                  <option value="">All prompts</option>
                  {promptOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <button
                type="button"
                className={`${BP_TOOLBAR_BUTTON_CLASSES} ml-auto`}
                onClick={() => {
                  setFilters({ ...EMPTY_AI_ANSWERS_FILTERS });
                  sendToParent({ type: 'update-query', query: {} });
                }}
              >
                Reset
              </button>
            </div>

            {/* Says what the selected purpose measures, so a filter row that is gone does not read as
              a fault. Silent on "All purposes" and on a purpose that measures everything. */}
            <PurposeKpiScopeNote term={selectedPurposeTerm} />

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-200/70 pt-3 text-sm">
              {/* A filter over a KPI this purpose does not measure is not offered at all — its answers
                cannot be sorted into "recommends you" and "does not" by a measurement nobody took. */}
              {showsRecommendationFilter ? (
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-500">Recommendation:</span>
                  {/* On a narrow screen the choices move under their name and scroll sideways there. */}
                  <div className={FILTER_CHOICE_STRIP_CLASSES}>
                    {recommendationTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={getBrandPresenceSubTabClassName(
                          maskedFilters.recommendation === tab.key,
                          BRAND_PRESENCE_TAB_IN_STRIP_CLASSES
                        )}
                        aria-pressed={maskedFilters.recommendation === tab.key}
                        onClick={() => updateFilters({ recommendation: tab.key })}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {showsVisibilityFilter ? (
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-500">Visibility:</span>
                  <div className={FILTER_CHOICE_STRIP_CLASSES}>
                    {visibilityTabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={getBrandPresenceSubTabClassName(
                          maskedFilters.visibility === tab.key,
                          BRAND_PRESENCE_TAB_IN_STRIP_CLASSES
                        )}
                        aria-pressed={maskedFilters.visibility === tab.key}
                        onClick={() => updateFilters({ visibility: tab.key })}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <label className="mb-0 flex cursor-pointer items-center gap-2 font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={filters.includeFailed}
                  onChange={(event) => updateFilters({ includeFailed: event.target.checked })}
                />
                Include failed{failedCount !== undefined ? ` (${failedCount})` : ''}
              </label>
              {englishLocaleKey ? (
                <label className="mb-0 flex cursor-pointer items-center gap-2 font-medium text-slate-600" title={PROMPT_LANGUAGE_HINT}>
                  <input
                    type="checkbox"
                    checked={filters.promptLanguage === 'local'}
                    onChange={(event) => updateFilters({ promptLanguage: event.target.checked ? 'local' : 'english' })}
                  />
                  Show prompts in their own language
                </label>
              ) : null}
              {/* Purely a display choice: both readings come from the citation mirror already on
                each row, so flipping this regroups in place without touching the network. */}
              <SourceGroupingToggle grouping={filters.sourceGrouping} onChange={(next) => updateFilters({ sourceGrouping: next })} />
            </div>
          </div>

          {optionsLoading ? (
            <SkeletonRegion label="Loading AI answers" className={`${BP_CARD_CLASSES} overflow-x-auto`}>
              {ANSWER_SKELETON_ROWS}
            </SkeletonRegion>
          ) : (
            <div className="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-slate-200/70">
              {/* The table scrolls sideways in its own container where its columns do not fit. The
                  container measures its own width, so a message in place of the rows is held to the
                  part in view. */}
              <div className="@container overflow-x-auto">
                {/* Fixed column proportions, so a long prompt or a long brand name cannot push the
                  other columns about from one page of answers to the next. */}
                <table className="w-full min-w-[60rem] table-fixed text-sm">
                  <AnswerColumns withStatus={filters.includeFailed} />
                  <thead>
                    <tr className="bg-slate-50/70 text-left">
                      <th className={BP_TABLE_HEAD_CELL_CLASSES}>When</th>
                      <th className={BP_TABLE_HEAD_CELL_CLASSES}>Prompt</th>
                      <th className={BP_TABLE_HEAD_CELL_CLASSES}>Engine</th>
                      <th className={BP_TABLE_HEAD_CELL_CLASSES}>Winner</th>
                      <th className={BP_TABLE_HEAD_CELL_CLASSES}>Mentions</th>
                      <th className={BP_TABLE_HEAD_CELL_CLASSES} title={SOURCES_COLUMN_HINT} aria-label={`Sources. ${SOURCES_COLUMN_HINT}`}>
                        Sources
                      </th>
                      {filters.includeFailed ? <th className={BP_TABLE_HEAD_CELL_CLASSES}>Status</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {runsLoading ? (
                      <>
                        <tr className="sr-only">
                          <td colSpan={COLUMN_COUNT}>Loading answers…</td>
                        </tr>
                        {ANSWER_SKELETON_TABLE_ROWS}
                      </>
                    ) : error ? (
                      <tr>
                        <td colSpan={COLUMN_COUNT} className="p-0">
                          <div className={MESSAGE_IN_VIEW_CLASSES}>
                            <div className="px-4 py-8 text-center text-rose-600">{error}</div>
                          </div>
                        </td>
                      </tr>
                    ) : !visibleRuns.length ? (
                      <tr>
                        <td colSpan={COLUMN_COUNT} className="p-0">
                          <div className={MESSAGE_IN_VIEW_CLASSES}>
                            <BrandPresenceEmptyState
                              icon={faComments}
                              title="No AI answers match these filters yet."
                              description="Answers appear here as your monitored prompts run — typically daily."
                            />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      visibleRuns.map((run) => {
                        const promptDisplay: PromptDisplay = {
                          local: promptLabels.get(promptRevisionId(run.prompt)) || UNKNOWN_PROMPT_LABEL,
                          // Server-resolved; absent when Sync Core could not resolve unambiguously.
                          english:
                            translationTextFor((run as unknown as { promptTranslations?: unknown }).promptTranslations, englishLocaleKey) ||
                            undefined,
                        };
                        const questionText = pickPromptText(promptDisplay, filters.promptLanguage);
                        const markOriginalLanguage = shouldMarkOriginalLanguage(promptDisplay, filters.promptLanguage);
                        const engineKey = referenceKey(run.engine);
                        const localeKey = referenceKey(run.locale);
                        const localeName = localeNameByKey.get(localeKey) || '';
                        const pending = isPendingRunStatus(run.status);
                        const askedAt = run.scheduledFor?.valueOf();
                        return (
                          <tr
                            key={String(run.id)}
                            className={`cursor-pointer align-middle transition-colors duration-150 hover:bg-slate-50/70 ${pending ? 'text-slate-400' : ''}`}
                            onClick={() => openRun(run)}
                          >
                            <td className={ANSWER_CELL_CLASSES}>
                              <time
                                className="whitespace-nowrap tabular-nums text-[0.8125rem] text-slate-600"
                                dateTime={askedAt !== undefined && Number.isFinite(askedAt) ? new Date(askedAt).toISOString() : undefined}
                                title={run.scheduledFor?.format('MMM DD, YYYY HH:mm')}
                              >
                                {formatRelativeTime(askedAt)}
                              </time>
                            </td>
                            <td className={ANSWER_CELL_CLASSES}>
                              <div className="line-clamp-2 font-semibold leading-snug text-slate-800">
                                {markOriginalLanguage ? (
                                  <FontAwesomeIcon
                                    icon={faGlobe}
                                    className="text-slate-400 me-1 text-[11px]"
                                    title={ORIGINAL_LANGUAGE_HINT}
                                    aria-label={ORIGINAL_LANGUAGE_HINT}
                                  />
                                ) : null}
                                {/* The keyboard's way into the answer; a pointer can press anywhere on the row. */}
                                <Link
                                  to={answerPath(run)}
                                  className="text-slate-800 no-underline transition-colors hover:text-primary hover:no-underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {questionText}
                                </Link>
                              </div>
                              <div className="mt-0.5 truncate text-xs text-slate-500">
                                {[translateCountry(run.country), localeName].filter(Boolean).join(' · ')}
                              </div>
                            </td>
                            <td className={ANSWER_CELL_CLASSES}>
                              {engineKey ? <EngineCell api={api} engineKey={engineKey} /> : <span className="text-slate-400">-</span>}
                            </td>
                            <td className={ANSWER_CELL_CLASSES}>
                              {pending ? (
                                <span
                                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500 motion-safe:animate-pulse"
                                  title="This answer is still being retrieved and analyzed"
                                >
                                  Analyzing…
                                </span>
                              ) : (
                                <WinnerCell run={run} api={api} entityDomains={entityDomains} />
                              )}
                            </td>
                            <td className={ANSWER_CELL_CLASSES}>
                              <MentionsCell run={run} api={api} showShare={showsShareOfVoice} showSentiment={showsSentiment} />
                            </td>
                            <td className={ANSWER_CELL_CLASSES}>
                              <SourcesCell
                                api={api}
                                run={run}
                                grouping={filters.sourceGrouping}
                                authoritySources={authoritySources}
                                sourcesReady={sourcesReady}
                                isActiveSource={isActiveSource}
                                onSelectSource={selectSource}
                              />
                            </td>
                            {filters.includeFailed ? (
                              <td className={ANSWER_CELL_CLASSES}>
                                <MonitoringPromptRunStatusBadge status={run.status} />
                              </td>
                            ) : null}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-slate-200/80 px-5 py-3.5 text-sm text-slate-500">
                <div>
                  Page {filters.page + 1} of {numberOfPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                    disabled={filters.page <= 0 || runsLoading}
                    onClick={() => updateFilters({ page: Math.max(0, filters.page - 1) })}
                  >
                    ‹ Prev
                  </button>
                  <button
                    type="button"
                    className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                    disabled={filters.page >= numberOfPages - 1 || runsLoading}
                    onClick={() => updateFilters({ page: Math.min(numberOfPages - 1, filters.page + 1) })}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </FaviconLoadGate>
  );
}
